import { CategoryPageSideFilters } from '@c/CategoryOrSubcategoryPage';
import useBikes from '@util/hooks/useBikes';
import { useAuth } from 'context/AuthContext';
import { Make, rideTypeChoices, UserBike } from 'models/shared';
import { useState } from 'react';
import Select from './Select';
import { PageSideFilters } from '@util/hooks/useProductsPage';
import Button from './Button';
import AddBikeModal from '@c/modals/AddGrarageModal';
import { ChevronRightIcon, PlusIcon } from '@c/icons';
import MobileDrawer from '@c/drawers/MobileDrawer';
import Link from 'next/link';
import { ArrowRightIcon } from '@heroicons/react/20/solid';

export const ShopByDirtBike = ({
  onBikeComplete,
  sideFilters,
  hideYourSavedBikes,
}: {
  onBikeComplete: (bike: UserBike) => void;
  sideFilters: CategoryPageSideFilters;
  hideYourSavedBikes?: boolean;
}) => {
  const { userDoc } = useAuth();
  const [bikeModalOpen, setBikeModalOpen] = useState(false);

  const initialBike = {
    year: sideFilters.bikeYears?.[0] ? +sideFilters.bikeYears[0] : undefined,
    make: sideFilters.bikeMakes?.[0] as Make | undefined,
    model: sideFilters.bikeModels?.[0],
    type: sideFilters.rideTypes?.[0],
  };

  const [selectedBike, setSelectedBike] = useState<Partial<UserBike>>({
    year: initialBike.year,
    make: initialBike.make,
    model: initialBike.model,
    type: initialBike.type,
    date_added: null,
    downloadUrl: '',
    imageUrl: '',
    in_feed: false,
  });

  return (
    <>
      <div className="flex w-full flex-col justify-between gap-y-12 pr-0 sm:mx-auto sm:max-w-content sm:flex-row sm:gap-y-0 sm:pr-6">
        <div className="flex flex-col gap-[1.6rem] sm:flex-row sm:items-center">
          <div className="hidden flex-col gap-2 font-semibold sm:flex">
            <span className="text-[1.6rem]">Find Parts for Your Ride</span>
            {!userDoc && (
              <Link
                href={`/login`}
                className="flex items-center gap-2 text-[1.3rem] text-brand-secondary hover:underline"
              >
                Log In to Add Your Ride
                <ChevronRightIcon className="h-8 w-8" />
              </Link>
            )}
          </div>
          <VehicleSelector
            selectedBike={selectedBike}
            setSelectedBike={setSelectedBike}
            onBikeComplete={onBikeComplete}
          />
        </div>

        {!!userDoc?.bikes?.length && !hideYourSavedBikes ? (
          <div className="flex items-center gap-x-2">
            <div className="flex items-center justify-center gap-[1.6rem] rounded-2xl border-2 sm:min-w-[30rem]">
              <Select
                allowEmpty={false}
                key={`${sideFilters.bikeYears?.[0]} ${
                  sideFilters.bikeMakes?.[0] ?? ''
                } ${sideFilters.bikeModels?.[0] ?? ''}`}
                placeholder="Your saved rides"
                options={
                  userDoc?.bikes?.length
                    ? userDoc?.bikes.map((bike, i) => ({
                        label: `${bike.year} ${bike.make} ${bike.model}`,
                        value: JSON.stringify({
                          year: bike.year,
                          make: bike.make,
                          model: bike.model,
                          type: bike.type,
                        }),
                        id: `${bike.year} ${bike.make} ${bike.model} ${i}`,
                      }))
                    : [
                        {
                          label: 'No bikes saved for this ride type',
                          value: 'No bikes saved for this ride type',
                          id: 'No bikes saved for this ride type',
                        },
                      ]
                }
                value={`${sideFilters.bikeYears?.[0]} ${
                  sideFilters.bikeMakes?.[0] ?? ''
                } ${sideFilters.bikeModels?.[0] ?? ''}`}
                onChange={(e) => {
                  const { year, make, model, type } = JSON.parse(e.value);
                  const bike = userDoc?.bikes?.find(
                    (bike) =>
                      bike.year === year &&
                      bike.make === make &&
                      bike.model === model &&
                      bike.type === type
                  );

                  onBikeComplete({
                    year: +year,
                    make,
                    model: model,
                    type: type,
                    date_added: null,
                    downloadUrl: '',
                    imageUrl: '',
                    in_feed: false,
                  });

                  if (bike?.type) {
                    sideFilters.rideTypes = [bike.type];
                  }
                }}
                className="!rounded-[2rem]"
              />
            </div>

            <div
              className="duration-125 flex h-[4rem] w-[4rem] cursor-pointer items-center justify-center rounded-2xl bg-brand-secondary transition-all hover:bg-brand-secondary-darker"
              onClick={() => setBikeModalOpen(true)}
            >
              <div className="flex h-[2rem] w-[2rem] items-center justify-center rounded-full bg-white">
                <PlusIcon className="text-brand-secondary" />
              </div>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-center gap-[1.6rem] sm:min-w-[30rem]">
            {!!userDoc && !!!userDoc?.bikes?.length && (
              <Button
                type="secondary"
                text="Add a Bike"
                onClick={() => setBikeModalOpen(true)}
              />
            )}
          </div>
        )}

        <AddBikeModal
          isOpen={bikeModalOpen}
          dismiss={() => setBikeModalOpen(false)}
        />
      </div>
    </>
  );
};

export const VehicleSelector = ({
  selectedBike,
  setSelectedBike,
  onBikeComplete,
}: {
  selectedBike: Partial<UserBike>;
  setSelectedBike: (bike: Partial<UserBike>) => void;
  onBikeComplete: (bike: UserBike) => void;
}) => {
  const { bikeYears, bikeMakes, bikeModels } = useBikes(
    selectedBike.year ? [`${selectedBike.year}`] : [],
    selectedBike.make ? [selectedBike.make as Make] : [],
    selectedBike.type ? [selectedBike.type] : []
  );

  return (
    <div className="flex justify-center">
      <div className="flex w-full flex-col items-center justify-center gap-[1.6rem] md:flex-row">
        <Select
          // disabled={!!initialBike.type}
          allowEmpty={false}
          placeholder="Select Ride Type"
          options={rideTypeChoices.map((type) => ({
            label: type,
            value: type,
            id: type,
          }))}
          value={`${selectedBike.type}`}
          onChange={(e) => {
            setSelectedBike({
              ...selectedBike,
              type: e.value,
            });
            if (selectedBike.make && selectedBike.model && selectedBike.type) {
              onBikeComplete({
                year: +e.value,
                make: selectedBike.make,
                model: selectedBike.model,
                type: selectedBike.type,
                date_added: null,
                downloadUrl: '',
                imageUrl: '',
                in_feed: false,
              });
            }
          }}
          className="!text-[2.1rem]"
        />

        {selectedBike.type !== 'Snow' && selectedBike.type !== 'Cycling' && (
          <>
            <Select
              allowEmpty={false}
              placeholder="Select Year"
              options={bikeYears.map((year) => ({
                label: `${year}`,
                value: `${year}`,
                id: `${year}`,
              }))}
              value={`${selectedBike.year}`}
              onChange={(e) => {
                setSelectedBike({
                  ...selectedBike,
                  year: e.value,
                });
                if (
                  selectedBike.make &&
                  selectedBike.model &&
                  selectedBike.type
                ) {
                  onBikeComplete({
                    year: +e.value,
                    make: selectedBike.make,
                    model: selectedBike.model,
                    type: selectedBike.type,
                    date_added: null,
                    downloadUrl: '',
                    imageUrl: '',
                    in_feed: false,
                  });
                }
              }}
            />
            <Select
              allowEmpty={false}
              placeholder="Select Make"
              options={bikeMakes.map((make) => ({
                label: make,
                value: make,
                id: make,
              }))}
              value={selectedBike.make}
              onChange={(e) => {
                setSelectedBike({
                  ...selectedBike,
                  make: e.value,
                });
                if (
                  selectedBike.make &&
                  selectedBike.model &&
                  selectedBike.type
                ) {
                  onBikeComplete({
                    year: +e.value,
                    make: selectedBike.make,
                    model: selectedBike.model,
                    type: selectedBike.type,
                    date_added: null,
                    downloadUrl: '',
                    imageUrl: '',
                    in_feed: false,
                  });
                }
              }}
            />
            <Select
              allowEmpty={false}
              placeholder={
                selectedBike.make &&
                `${selectedBike.year}` !== '' &&
                bikeModels.length === 0
                  ? 'No Bikes Found'
                  : 'Select Model'
              }
              options={bikeModels
                .sort((a, b) => {
                  // find number in model string
                  const aNumber = a.match(/\d+/);
                  const bNumber = b.match(/\d+/);
                  if (aNumber && bNumber) {
                    return +aNumber[0] - +bNumber[0];
                  }
                  return 0;
                })
                .map((model) => ({
                  label: model,
                  value: model,
                  id: model,
                }))}
              value={selectedBike.model || undefined}
              onChange={(e) => {
                setSelectedBike({
                  ...selectedBike,
                  model: e.value,
                });
                if (
                  selectedBike.year &&
                  selectedBike.make &&
                  selectedBike.type
                ) {
                  onBikeComplete({
                    year: +selectedBike.year,
                    make: selectedBike.make,
                    model: e.value,
                    type: selectedBike.type,
                    date_added: null,
                    downloadUrl: '',
                    imageUrl: '',
                    in_feed: false,
                  });
                }
              }}
              disabled={bikeModels.length === 0}
            />
          </>
        )}
      </div>
    </div>
  );
};

export const ShopByVehicle = ({
  sideFilters,
  onBikeComplete,
}: {
  sideFilters: PageSideFilters;
  onBikeComplete: (bike: UserBike) => void;
}) => {
  const [openModal, setOpenModal] = useState<'' | 'shop-by-bike'>('');
  return (
    <>
      <div className="hidden w-full flex-col gap-[1.6rem] bg-[#edededfe] px-4 py-4 sm:flex sm:flex-row sm:items-center sm:rounded-[1.2rem] sm:px-default-padding-x">
        <ShopByDirtBike
          sideFilters={sideFilters}
          onBikeComplete={onBikeComplete}
        />
      </div>

      <div className="mb-8 w-full sm:hidden">
        <div className="flex w-full items-center justify-between bg-[#F6F6F6] px-[2rem] py-[1.5rem]">
          <span className="text-[2rem] font-semibold text-black">
            Find Parts for Your Ride{' '}
          </span>

          <button
            className="flex items-center justify-center gap-x-2 rounded-[1rem] bg-brand-secondary px-6 py-4 text-[1.8rem] font-semibold text-white"
            type="button"
            onClick={() => setOpenModal('shop-by-bike')}
          >
            Add
            <PlusIcon className="h-10 w-10 text-white" />
          </button>
        </div>

        <MobileDrawer
          show={openModal === 'shop-by-bike'}
          dismiss={() => setOpenModal('')}
          title="Find Parts for Your Ride"
        >
          <div className="flex h-[50rem] flex-col gap-[1.6rem] p-8">
            <ShopByDirtBike
              sideFilters={sideFilters}
              onBikeComplete={(bike) => {
                onBikeComplete(bike);
                setOpenModal('');
              }}
            />
          </div>
        </MobileDrawer>
      </div>
    </>
  );
};
